export interface ProviderDefinitionFieldShape {
  fieldKey: string;
  label: string;
  inputType: string;
  required?: boolean;
  secret?: boolean;
  description?: string;
  placeholder?: string;
  section?: string;
  fieldOrder?: number;
  attributes?: Record<string, unknown>;
}

export interface IntegrationConnectionConfigShape {
  additional?: Record<string, unknown>;
  [key: string]: unknown;
}

const knownConfigKeys = [
  'instanceUrl',
  'siteUrl',
  'clientId',
  'clientSecretId',
  'keyId',
  'workspaceId',
  'enterpriseId',
  'tenantId',
  'teamId',
  'appId',
  'botUserId',
  'manifestAppId',
  'personalTabEntityId',
  'signingSecretId',
  'accessTokenSecretId',
  'cloudId',
  'userEmail',
  'mode',
  'baseUrl',
  'portalName',
  'customerId',
  'username',
  'passwordSecretId',
  'tokenUrl',
  'grantCodeSecretId',
  'refreshTokenSecretId',
  'apiKeySecretId'
] as const;

export const integrationConfigKeys = [...knownConfigKeys];

const isKnownConfigKey = (key: string): key is (typeof knownConfigKeys)[number] =>
  knownConfigKeys.includes(key as (typeof knownConfigKeys)[number]);

const matchesVisibleWhenRule = (rule: unknown, values: Record<string, string>) => {
  if (!rule || typeof rule !== 'object') {
    return true;
  }
  const ruleRecord = rule as Record<string, unknown>;
  const field = typeof ruleRecord.field === 'string' ? ruleRecord.field : '';
  if (!field) {
    return true;
  }
  const currentValue = values[field] ?? '';
  const equals = ruleRecord.equals;
  if (typeof equals === 'string') {
    return currentValue === equals;
  }
  if (Array.isArray(equals)) {
    return equals.some((item) => typeof item === 'string' && item === currentValue);
  }
  const notEquals = ruleRecord.notEquals;
  if (typeof notEquals === 'string') {
    return currentValue !== notEquals;
  }
  if (Array.isArray(notEquals)) {
    return !notEquals.some((item) => typeof item === 'string' && item === currentValue);
  }
  return true;
};

export const isIntegrationConfigFieldVisible = (field: ProviderDefinitionFieldShape, values: Record<string, string>) => {
  const visibleWhen = field.attributes?.visibleWhen;
  if (Array.isArray(visibleWhen)) {
    return visibleWhen.every((rule) => matchesVisibleWhenRule(rule, values));
  }
  return matchesVisibleWhenRule(visibleWhen, values);
};

export const getIntegrationConfigValue = (config: IntegrationConnectionConfigShape | undefined, key: string) => {
  if (!config) {
    return '';
  }
  const value = config[key];
  if (typeof value === 'string') {
    return value;
  }
  const extra = config.additional?.[key];
  return typeof extra === 'string' ? extra : '';
};

export interface BuildIntegrationConnectionPayloadInput {
  provider: string;
  values: Record<string, string>;
  fields: ProviderDefinitionFieldShape[];
  existingConfig?: IntegrationConnectionConfigShape;
  createSecret: (value: string, label: string) => Promise<string>;
}

export const buildIntegrationConnectionPayload = async ({
  provider,
  values,
  fields,
  existingConfig,
  createSecret
}: BuildIntegrationConnectionPayloadInput): Promise<Record<string, unknown>> => {
  const payload: Record<string, unknown> = {};
  const additional: Record<string, unknown> = { ...(existingConfig?.additional ?? {}) };

  for (const field of fields) {
    if (isIntegrationConfigFieldVisible(field, values)) {
      continue;
    }
    delete additional[field.fieldKey];
  }

  for (const field of fields) {
    if (!isIntegrationConfigFieldVisible(field, values)) {
      continue;
    }
    const rawValue = (values[field.fieldKey] ?? '').trim();
    const persistValue = (value: string) => {
      if (isKnownConfigKey(field.fieldKey)) {
        payload[field.fieldKey] = value;
        return;
      }
      additional[field.fieldKey] = value;
    };
    if (field.secret) {
      if (rawValue) {
        persistValue(await createSecret(rawValue, `${provider} ${field.label}`.toLowerCase()));
      } else {
        const existingValue = getIntegrationConfigValue(existingConfig, field.fieldKey);
        if (existingValue) {
          persistValue(existingValue);
        }
      }
      continue;
    }
    if (rawValue) {
      persistValue(rawValue);
    }
  }

  if (Object.keys(additional).length > 0) {
    payload.additional = additional;
  }
  return payload;
};
